<template>
  <v-container fluid>

    <!-- Header -->
    <div class="d-flex align-center mb-4" style="gap:12px">
      <v-btn small outlined color="info" tile @click="$router.back()">
        <v-icon left>mdi-arrow-left-bold</v-icon>Volver
      </v-btn>
      <div>
        <div class="text-h6 font-weight-bold">Solicitudes de Dado de Baja</div>
        <div class="caption grey--text">Bandeja de aprobación — programas solicitados para baja</div>
      </div>
      <v-spacer />
      <v-btn small outlined :loading="loading" @click="fetchSolicitudes">
        <v-icon small left>mdi-refresh</v-icon> Actualizar
      </v-btn>
    </div>

    <!-- Filtro de estado -->
    <div class="d-flex align-center mb-3" style="gap:10px">
      <span class="caption grey--text">Filtrar por estado:</span>
      <v-btn-toggle v-model="filtroEstado" mandatory dense @change="onFiltroChange">
        <v-btn value="SOLICITADO" small>
          <v-icon left small color="yellow darken-3">mdi-clock-outline</v-icon> Pendientes
        </v-btn>
        <v-btn value="APROBADO" small>
          <v-icon left small color="red darken-1">mdi-close-circle</v-icon> Aprobados
        </v-btn>
        <v-btn value="RECHAZADO" small>
          <v-icon left small color="grey">mdi-cancel</v-icon> Rechazados
        </v-btn>
        <v-btn value="" small>
          <v-icon left small>mdi-format-list-bulleted</v-icon> Todos
        </v-btn>
      </v-btn-toggle>
      <v-chip x-small class="ml-auto">Total: {{ total }}</v-chip>
    </div>

    <!-- Tabla -->
    <v-card outlined>
      <v-progress-linear v-if="loading" indeterminate color="red darken-1" />
      <v-data-table
        :headers="headers"
        :items="solicitudes"
        :loading="loading"
        :server-items-length="total"
        :footer-props="{ itemsPerPageOptions: [10, 20, 50] }"
        :items-per-page.sync="perPage"
        :page.sync="page"
        dense
        @update:page="fetchSolicitudes"
        @update:items-per-page="fetchSolicitudes"
      >
        <template v-slot:item.programa="{ item }">
          <div>
            <v-chip x-small color="blue-grey darken-1" dark label class="mb-1 font-weight-bold">
              {{ item.programa_codigo || '—' }}
            </v-chip>
            <div class="caption" style="max-width:220px;white-space:normal;line-height:1.3">
              {{ item.programa_nombre_compuesto }}
            </div>
            <div class="caption grey--text">{{ item.sede_nombre }}</div>
          </div>
        </template>

        <template v-slot:item.motivo="{ item }">
          <div class="caption">{{ item.motivo_baja_programa_nombre }}</div>
          <div v-if="item.observacion" class="caption grey--text" style="max-width:180px;white-space:normal">
            "{{ item.observacion }}"
          </div>
        </template>

        <template v-slot:item.solicitante="{ item }">
          <div class="caption">{{ item.solicitante_nombres }} {{ item.solicitante_primer_apellido }}</div>
          <div class="caption grey--text">{{ (item.fecha_registro || '').slice(0, 10) }}</div>
        </template>

        <template v-slot:item.estado="{ item }">
          <v-chip x-small dark :color="colorEstado(item.estado)">{{ item.estado }}</v-chip>
          <div v-if="item.fecha_respuesta_solicitud" class="caption grey--text mt-1">
            {{ (item.fecha_respuesta_solicitud || '').slice(0, 10) }}
          </div>
        </template>

        <template v-slot:item.respuesta="{ item }">
          <div v-if="item.respuesta" class="caption" style="max-width:160px;white-space:normal">
            {{ item.respuesta }}
          </div>
          <span v-else class="caption grey--text">—</span>
        </template>

        <template v-slot:item.acciones="{ item }">
          <div v-if="item.estado === 'SOLICITADO' && esAprobador" class="d-flex" style="gap:4px">
            <v-btn x-small color="green darken-1" dark @click="abrirAprobar(item)">
              <v-icon left x-small>mdi-check</v-icon> Aprobar
            </v-btn>
            <v-btn x-small color="red darken-1" outlined @click="abrirRechazar(item)">
              <v-icon left x-small>mdi-close</v-icon> Rechazar
            </v-btn>
          </div>
          <span v-else-if="item.estado !== 'SOLICITADO'" class="caption grey--text">—</span>
          <span v-else class="caption grey--text">Sin permiso</span>
        </template>

        <template v-slot:no-data>
          <div class="text-center grey--text py-6">
            <v-icon large color="grey lighten-2">mdi-check-circle-outline</v-icon>
            <div class="mt-2 caption">No hay solicitudes en este estado</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialog: confirmar aprobar -->
    <v-dialog v-model="dialogAprobar" max-width="480" persistent>
      <v-card v-if="itemSeleccionado">
        <v-toolbar color="green darken-1" dark dense flat>
          <v-icon class="mr-2">mdi-check-circle-outline</v-icon>
          <v-toolbar-title>Aprobar dado de baja</v-toolbar-title>
          <v-spacer />
          <v-btn icon :disabled="loadingAccion" @click="dialogAprobar = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <div class="body-2 font-weight-bold mb-1">{{ itemSeleccionado.programa_nombre_compuesto }}</div>
          <div class="caption grey--text mb-3">{{ itemSeleccionado.sede_nombre }}</div>

          <v-alert v-if="inscritosEfectivos > 0" type="warning" dense outlined icon="mdi-alert-circle">
            Al aprobar, <strong>{{ inscritosEfectivos }} inscripción(es) activa(s)</strong> pasarán automáticamente a <strong>RETIRADO</strong>.
          </v-alert>
          <v-progress-circular v-if="loadingInscritos" indeterminate color="warning" size="20" class="d-block mx-auto mb-2" />

          <div class="caption mb-2">
            <strong>Causa:</strong> {{ itemSeleccionado.motivo_baja_programa_nombre }}<br>
            <span v-if="itemSeleccionado.observacion"><strong>Observación:</strong> {{ itemSeleccionado.observacion }}</span>
          </div>
          <p class="body-2">¿Confirmas que deseas aprobar esta solicitud?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="loadingAccion" @click="dialogAprobar = false">Cancelar</v-btn>
          <v-btn color="green darken-1" dark :loading="loadingAccion" @click="ejecutarAprobar">
            <v-icon left small>mdi-check</v-icon> Confirmar aprobación
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: rechazar -->
    <v-dialog v-model="dialogRechazar" max-width="440" persistent>
      <v-card v-if="itemSeleccionado">
        <v-toolbar color="red darken-1" dark dense flat>
          <v-icon class="mr-2">mdi-close-circle-outline</v-icon>
          <v-toolbar-title>Rechazar solicitud</v-toolbar-title>
          <v-spacer />
          <v-btn icon :disabled="loadingAccion" @click="dialogRechazar = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <div class="caption grey--text mb-3">{{ itemSeleccionado.programa_nombre_compuesto }}</div>
          <v-textarea
            v-model="formRechazar.respuesta"
            label="Motivo del rechazo *"
            outlined dense rows="3" auto-grow
            hint="Campo obligatorio"
            persistent-hint
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="loadingAccion" @click="dialogRechazar = false">Cancelar</v-btn>
          <v-btn
            color="red darken-1" dark
            :loading="loadingAccion"
            :disabled="!formRechazar.respuesta"
            @click="ejecutarRechazar"
          >
            <v-icon left small>mdi-close</v-icon> Rechazar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="5000">
      {{ snackbar.msg }}
      <template v-slot:action>
        <v-btn text small @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script>
import xaxios from '@/xaxios';

const API_URL = `${process.env.VUE_APP_API_REST}/api/v1`;

export default {
  name: 'SolicitudesDadoDeBajaListado',

  data() {
    return {
      solicitudes: [],
      total: 0,
      page: 1,
      perPage: 20,
      filtroEstado: 'SOLICITADO',
      loading: false,
      headers: [
        { text: 'Programa / Sede', value: 'programa', sortable: false, width: '240px' },
        { text: 'Causa / Observación', value: 'motivo', sortable: false, width: '200px' },
        { text: 'Solicitante', value: 'solicitante', sortable: false, width: '140px' },
        { text: 'Estado', value: 'estado', sortable: false, width: '110px' },
        { text: 'Respuesta', value: 'respuesta', sortable: false, width: '160px' },
        { text: 'Acciones', value: 'acciones', sortable: false, width: '170px' },
      ],
      dialogAprobar: false,
      dialogRechazar: false,
      itemSeleccionado: null,
      loadingAccion: false,
      inscritosEfectivos: 0,
      loadingInscritos: false,
      formRechazar: { respuesta: '' },
      snackbar: { show: false, msg: '', color: 'success' },
    };
  },

  computed: {
    esAprobador() {
      const cargo = this.$store.state.CARGO;
      return cargo && [1, 5, 26].includes(cargo.id);
    },
  },

  mounted() {
    this.fetchSolicitudes();
  },

  methods: {
    onFiltroChange() {
      this.page = 1;
      this.fetchSolicitudes();
    },

    async fetchSolicitudes() {
      this.loading = true;
      try {
        const cargos = this.$store.state.CARGOS || [];
        const params = {
          page: this.page,
          per_page: this.perPage,
          sedes_ids: cargos.length ? [...new Set(cargos.map(c => c.sede?.id).filter(Boolean))].join(',') : undefined,
        };
        if (this.filtroEstado) params.estado = this.filtroEstado;
        const { data } = await xaxios.get(`${API_URL}/dado-de-baja/solicitudes`, { params });
        this.solicitudes = data?.data?.data || [];
        this.total = data?.data?.total || 0;
      } catch (e) {
        window.console.error('Error cargando solicitudes de baja:', e);
        this.notificar('Error al cargar las solicitudes', 'error');
      } finally {
        this.loading = false;
      }
    },

    async abrirAprobar(item) {
      this.itemSeleccionado = item;
      this.inscritosEfectivos = 0;
      this.dialogAprobar = true;
      this.loadingInscritos = true;
      try {
        const { data } = await xaxios.get(`${API_URL}/programas`, {
          params: { programa_id: item.programa_id, per_page: 1, page: 1 },
        });
        const lista = data?.data || [];
        if (lista.length > 0) {
          this.inscritosEfectivos = lista[0].inscritos_efectivos || 0;
        }
      } catch (e) {
        window.console.error('Error consultando inscritos:', e);
      } finally {
        this.loadingInscritos = false;
      }
    },

    async ejecutarAprobar() {
      if (!this.itemSeleccionado) return;
      this.loadingAccion = true;
      try {
        await xaxios.patch(`${API_URL}/dado-de-baja/solicitudes/${this.itemSeleccionado.id}`, { estado: 'APROBADO' });
        this.dialogAprobar = false;
        this.notificar('Solicitud aprobada. El programa fue dado de baja.', 'success');
        await this.fetchSolicitudes();
      } catch (err) {
        const status = err?.response?.status;
        if (status === 403) {
          this.notificar('No tienes permiso para aprobar el dado de baja en esta sede.', 'error');
        } else {
          const msg = err?.response?.data?.error || 'Error al aprobar la solicitud';
          this.notificar(msg, 'error');
        }
      } finally {
        this.loadingAccion = false;
      }
    },

    abrirRechazar(item) {
      this.itemSeleccionado = item;
      this.formRechazar.respuesta = '';
      this.dialogRechazar = true;
    },

    async ejecutarRechazar() {
      if (!this.itemSeleccionado || !this.formRechazar.respuesta) return;
      this.loadingAccion = true;
      try {
        await xaxios.patch(`${API_URL}/dado-de-baja/solicitudes/${this.itemSeleccionado.id}`, {
          estado: 'RECHAZADO',
          respuesta: this.formRechazar.respuesta,
        });
        this.dialogRechazar = false;
        this.notificar('Solicitud rechazada.', 'info');
        await this.fetchSolicitudes();
      } catch (err) {
        const status = err?.response?.status;
        if (status === 403) {
          this.notificar('No tienes permiso para rechazar en esta sede.', 'error');
        } else {
          const msg = err?.response?.data?.error || 'Error al rechazar la solicitud';
          this.notificar(msg, 'error');
        }
      } finally {
        this.loadingAccion = false;
      }
    },

    colorEstado(estado) {
      if (estado === 'APROBADO') return 'red darken-1';
      if (estado === 'RECHAZADO') return 'grey darken-1';
      return 'yellow darken-3';
    },

    notificar(msg, color = 'success') {
      this.snackbar = { show: true, msg, color };
    },
  },
};
</script>
